import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import {
  ChevronLeft, ChevronDown, Pencil, Calendar, Mail, Key, Archive, Eye, EyeOff, Shield, KeyRound,
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';
import {
  OverflowMenu, Dialog, Field, PersonAvatar, StatusPill, ErrorState, fmtDate, num,
} from '../../components/ui';
import {
  roleLabel, employmentLabel, ndisScreening, tonePill, staffName, forgetStaffRecord, useStableCallback,
} from './hrKit';

// The record's tabs. Six are always in view; the rest live under "More" (and whichever one you are
// on is shown in the bar too, so you can always see where you are).
const MAIN_TABS = [
  { label: 'Overview', path: '' },
  { label: 'Documents', path: '/documents', count: 'documents' },
  { label: 'Training', path: '/training' },
  { label: 'Shifts', path: '/employee-shifts' },
  { label: 'Leave', path: '/leave' },
  { label: 'Notes', path: '/notes' },
];
const MORE_TABS = [
  { label: 'Calendar', path: '/calendar' },
  { label: 'Available shifts', path: '/available-shifts' },
  { label: 'Availability', path: '/availability' },
  { label: 'Appraisal', path: '/appraisal' },
  { label: 'Warnings', path: '/warnings' },
  { label: 'Incidents', path: '/incidents' },
  { label: 'Communications', path: '/communications' },
];

const TAB_CLS = (on) => clsx(
  '-mb-px inline-flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm font-medium whitespace-nowrap transition-colors duration-150',
  on ? 'border-primary-600 text-slate-900' : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-800',
);

function ManageTrigger() {
  return <span className="inline-flex items-center gap-1.5">Manage<ChevronDown size={13} aria-hidden="true" /></span>;
}
function MoreTrigger() {
  return <span className="inline-flex items-center gap-1">More<ChevronDown size={13} aria-hidden="true" /></span>;
}

function HeaderSkeleton() {
  return (
    <div className="mb-6" aria-busy="true" aria-label="Loading staff member">
      <div className="skeleton h-4 w-16" />
      <div className="mt-4 flex items-center gap-4">
        <div className="skeleton h-16 w-16 rounded-xl" />
        <div className="flex-1 space-y-2">
          <div className="skeleton h-6 w-56" />
          <div className="skeleton h-4 w-40" />
        </div>
      </div>
      <div className="skeleton mt-4 h-16 w-full rounded-xl" />
      <div className="mt-4 h-10 border-b border-slate-200" />
    </div>
  );
}

function Fact({ label, children }) {
  return (
    <div className="min-w-0">
      <dt className="text-[12px] font-medium text-slate-500">{label}</dt>
      <dd className="mt-0.5 truncate text-sm font-medium text-slate-800">{children}</dd>
    </div>
  );
}
const none = <span className="font-normal text-slate-400">—</span>;

export default function StaffHeader({ staff, error, onRetry }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [inviteSending, setInviteSending] = useState(false);
  const [passOpen, setPassOpen] = useState(false);
  const [newPass, setNewPass] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [passError, setPassError] = useState('');
  const [passSaving, setPassSaving] = useState(false);
  const { user: me } = useAuth();
  // Set once this page has reset the person's 2FA, so the menu reflects it without a reload.
  const [mfaResetDone, setMfaResetDone] = useState(false);

  const closePass = useStableCallback(() => {
    if (passSaving) return;
    setPassOpen(false); setNewPass(''); setPassError(''); setShowPass(false);
  });

  if (!staff) {
    if (error) {
      return (
        <div className="mb-6">
          <Link to="/staff" className="inline-flex items-center gap-1 text-[13px] font-medium text-slate-500 hover:text-slate-800">
            <ChevronLeft size={14} aria-hidden="true" />Staff
          </Link>
          <div className="mt-4 rounded-xl border border-slate-200 bg-white"><ErrorState message={error} onRetry={onRetry} /></div>
        </div>
      );
    }
    return <HeaderSkeleton />;
  }

  const name = staffName(staff) || 'Staff member';
  const base = `/staff/${staff.id}`;
  const screening = ndisScreening(staff, staff.ndis_screening_document ? [staff.ndis_screening_document] : []);
  const docs = staff.documents_summary;

  async function archiveStaff() {
    const ok = await confirm(
      `${name} will be taken off active lists and rostering. Their records are kept, and you can restore them from Archived staff.`,
      { title: `Archive ${name}?`, confirmLabel: 'Archive', danger: true },
    );
    if (!ok) return;
    try {
      await api.delete(`/staff/${staff.id}`);
      forgetStaffRecord(staff.id);
      toast.success(`${name} archived`);
      navigate('/staff');
    } catch (e) { toast.error(e.response?.data?.message || "Couldn't archive this staff member. Try again."); }
  }

  async function sendAppInvite() {
    if (!staff.email) { toast.error(`${name} has no email address. Add one in Edit first.`); return; }
    setInviteSending(true);
    try {
      await api.post('/auth/invite/send', { userId: staff.id });
      toast.success(`App invite sent to ${staff.email}`);
    } catch (e) {
      toast.error(e.response?.data?.message || "The invite didn't send. Try again.");
    } finally {
      setInviteSending(false);
    }
  }

  async function handleSetPassword(e) {
    e.preventDefault();
    if (newPass.length < 8) { setPassError('Use at least 8 characters.'); return; }
    setPassSaving(true);
    try {
      await api.post('/auth/admin/set-password', { userId: staff.id, password: newPass });
      toast.success(`App password set for ${name}`);
      setPassSaving(false);
      setPassOpen(false); setNewPass(''); setShowPass(false);
    } catch (err) {
      setPassError(err.response?.data?.message || "The password wasn't saved. Try again.");
      setPassSaving(false);
    }
  }

  // Reset two-factor sign-in: for an administrator helping someone who lost their phone, or who was
  // locked out when the organisation's 2FA setup period ended. The server re-checks all of this
  // (same organisation, real admin, account owner and super admin protected).
  const isSuperCaller = me?.role === 'super_admin' || me?.impersonating === true;
  const canResetMfa = ['admin', 'super_admin'].includes(me?.role)
    && String(me?.id) !== String(staff.id)
    && (staff.mfa_enabled || ['admin', 'office_user', 'super_admin'].includes(staff.role))
    && (isSuperCaller || (!staff.is_account_owner && staff.role !== 'super_admin'));

  async function resetMfa() {
    const ok = await confirm(
      `This turns off two-factor sign-in for ${name} and cancels their recovery codes. They can sign in with just their password, then set it up again from My account › Security. If your organisation requires 2FA, they get 14 days to do that.`,
      { title: `Reset two-factor sign-in for ${name}?`, confirmLabel: 'Reset two-factor', danger: true },
    );
    if (!ok) return;
    try {
      const { data } = await api.post(`/auth/mfa/reset/${staff.id}`);
      setMfaResetDone(true);
      toast.success(data?.message || `Two-factor sign-in reset for ${name}`);
    } catch (e) { toast.error(e.response?.data?.message || "Couldn't reset two-factor sign-in. Try again."); }
  }

  const isOn = (path) => (path === ''
    ? pathname === base || pathname === `${base}/details`
    : pathname === base + path || pathname.startsWith(`${base + path}/`));
  const activeMore = MORE_TABS.find((t) => isOn(t.path));

  const subtitle = [staff.job_title, roleLabel(staff.role), staff.employment_type ? employmentLabel(staff.employment_type) : null]
    .filter((v) => v && v !== '—')
    .filter((v, i, a) => a.indexOf(v) === i)
    .join(' · ');

  return (
    <>
      <div className="mb-6">
        <Link to="/staff" className="inline-flex items-center gap-1 text-[13px] font-medium text-slate-500 hover:text-slate-800">
          <ChevronLeft size={14} aria-hidden="true" />Staff
        </Link>

        {/* Identity */}
        <div className="mt-3 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 items-center gap-4">
            <PersonAvatar id={staff.id} photo={staff.profile_photo_url} firstName={staff.first_name} lastName={staff.last_name} size="xl" square />
            <div className="min-w-0">
              <h1 className="truncate text-[22px] font-semibold leading-tight tracking-[-0.02em] text-slate-900">{name}</h1>
              {subtitle && <p className="mt-0.5 truncate text-sm text-slate-500">{subtitle}</p>}
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <StatusPill status={staff.is_active ? 'active' : 'inactive'} />
                {staff.on_hold && <span className="badge-yellow whitespace-nowrap" title="Excluded from rostering">On hold</span>}
                <span className={clsx(tonePill(screening.tone), 'whitespace-nowrap')} title={screening.label}>
                  <Shield size={11} aria-hidden="true" />{screening.short}
                </span>
              </div>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Link to={`${base}/edit`} className="btn-secondary btn-sm"><Pencil size={13} aria-hidden="true" />Edit</Link>
            <OverflowMenu
              label={`Manage ${name}`}
              icon={ManageTrigger}
              className="btn-secondary btn-sm !text-slate-700"
              items={[
                { label: 'Schedule a shift', icon: Calendar, onClick: () => navigate(`/scheduler?staffId=${staff.id}`) },
                { divider: true },
                { label: inviteSending ? 'Sending invite…' : 'Send app invite', icon: Mail, onClick: sendAppInvite, disabled: inviteSending },
                { label: 'Set app password', icon: Key, onClick: () => setPassOpen(true) },
                ...(canResetMfa ? [{
                  label: mfaResetDone ? 'Two-factor sign-in reset' : 'Reset two-factor sign-in',
                  icon: KeyRound, onClick: resetMfa, disabled: mfaResetDone,
                }] : []),
                { divider: true },
                { label: 'Archive staff member', icon: Archive, onClick: archiveStaff, danger: true },
              ]}
            />
          </div>
        </div>

        {/* Key facts — each shown once on the record */}
        <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 rounded-xl border border-slate-200 bg-white px-4 py-3 sm:grid-cols-3 lg:grid-cols-5">
          <Fact label="Phone">{staff.phone ? <a href={`tel:${staff.phone}`} className="hover:text-primary-700 hover:underline">{staff.phone}</a> : none}</Fact>
          <Fact label="Email">{staff.email ? <a href={`mailto:${staff.email}`} className="hover:text-primary-700 hover:underline" title={staff.email}>{staff.email}</a> : none}</Fact>
          <Fact label="Start date">{staff.start_date ? fmtDate(String(staff.start_date).slice(0, 10)) : none}</Fact>
          <Fact label="Manager">{staff.manager_name || none}</Fact>
          <Fact label="Completed shifts"><span className="tabular-nums">{num(staff.total_shifts_completed || 0)}</span></Fact>
        </dl>

        {/* Tabs — real links, so any tab can be bookmarked or opened in a new window */}
        <nav aria-label="Staff record" className="mt-4 flex items-end gap-1 overflow-x-auto border-b border-slate-200 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {MAIN_TABS.map((t) => {
            const on = isOn(t.path);
            return (
              <Link key={t.label} to={base + t.path} aria-current={on ? 'page' : undefined} className={TAB_CLS(on)}>
                {t.label}
                {t.count === 'documents' && docs && docs.total > 0 && (
                  <span className={clsx('rounded-full px-1.5 text-[11px] font-semibold tabular-nums', on ? 'bg-primary-50 text-primary-700' : 'bg-slate-100 text-slate-500')}>{num(docs.total)}</span>
                )}
                {t.count === 'documents' && docs && docs.expired > 0 && (
                  <span className="text-[11.5px] font-semibold text-rose-600">{num(docs.expired)} expired</span>
                )}
                {t.count === 'documents' && docs && !docs.expired && docs.expiring > 0 && (
                  <span className="text-[11.5px] font-semibold text-amber-700">{num(docs.expiring)} expiring</span>
                )}
              </Link>
            );
          })}
          {activeMore && (
            <Link to={base + activeMore.path} aria-current="page" className={TAB_CLS(true)}>{activeMore.label}</Link>
          )}
          <OverflowMenu
            label="More sections"
            icon={MoreTrigger}
            className="-mb-px shrink-0 rounded-none border-b-2 border-transparent px-3 py-2.5 text-sm font-medium !text-slate-500 hover:!bg-transparent hover:!text-slate-800"
            items={MORE_TABS.map((t) => ({ label: t.label, onClick: () => navigate(base + t.path), disabled: isOn(t.path) }))}
          />
        </nav>
      </div>

      {/* Set app password */}
      <Dialog open={passOpen} onClose={closePass} size="sm" title="Set app password"
        description={`${name} signs in to the mobile app with this password.`}
        footer={(
          <>
            <button type="button" onClick={closePass} className="btn-secondary" disabled={passSaving}>Cancel</button>
            <button type="submit" form="staff-set-password" className="btn-primary" disabled={passSaving}>{passSaving ? 'Saving…' : 'Set password'}</button>
          </>
        )}>
        <form id="staff-set-password" onSubmit={handleSetPassword}>
          <Field label="New password" required error={passError} hint="At least 8 characters.">
            {(id) => (
              <div className="relative">
                <input id={id} type={showPass ? 'text' : 'password'} autoComplete="new-password" className="input-field pr-10"
                  value={newPass} onChange={(e) => { setNewPass(e.target.value); if (passError) setPassError(''); }} />
                <button type="button" onClick={() => setShowPass((s) => !s)} aria-label={showPass ? 'Hide password' : 'Show password'}
                  className="absolute right-1.5 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100">
                  {showPass ? <EyeOff size={15} aria-hidden="true" /> : <Eye size={15} aria-hidden="true" />}
                </button>
              </div>
            )}
          </Field>
        </form>
      </Dialog>
    </>
  );
}
